import { useState, useMemo, useRef, useCallback } from 'react';
import { useWindowDimensions, LayoutRectangle } from 'react-native';
import { TTooltipContext } from './TooltipContext';
import { STATUS, TTooltipState } from './TooltipProvider.types';
import { calculateTooltipAndArrowStyle } from './TooltipProvider.utils';

export const useTooltipProviderLogic = () => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const [tooltipState, setTooltipState] = useState<TTooltipState>({
    layout: undefined,
    tooltip: undefined,
    isVisible: false,
  });

  const statusRef = useRef<STATUS>(STATUS.INITIAL);

  const setTooltipLayout = useCallback(
    (layout: LayoutRectangle) => {
      if (statusRef.current === STATUS.INITIAL) {
        statusRef.current = STATUS.IDLE;
      }
      setTooltipState(prev => {
        return { ...prev, layout, isVisible: statusRef.current === STATUS.OPENING };
      });
    },
    [setTooltipState],
  );

  const openTooltip: TTooltipContext['openTooltip'] = useCallback(
    tooltip => {
      if (statusRef.current === STATUS.INITIAL) {
        statusRef.current = STATUS.IDLE;
      } else {
        statusRef.current = STATUS.OPENING;
      }
      setTooltipState(prev => {
        return { ...prev, tooltip, isVisible: false };
      });
    },
    [setTooltipState],
  );

  const closeTooltip: TTooltipContext['closeTooltip'] = useCallback(() => {
    statusRef.current = STATUS.CLOSING;
    setTooltipState(prev => {
      return { ...prev, tooltip: undefined, isVisible: false };
    });
  }, [setTooltipState]);

  const { tooltipStyle, arrowStyle } = useMemo(
    () => calculateTooltipAndArrowStyle(tooltipState, screenWidth, screenHeight),
    [tooltipState],
  );

  const contextValue = useMemo(() => ({ openTooltip, closeTooltip }), [openTooltip, closeTooltip]);

  return {
    TooltipContent: !!tooltipState?.layout && tooltipState?.tooltip?.TooltipContent,
    contextValue,
    setTooltipLayout,
    tooltipStyle,
    arrowStyle,
    tooltipId: tooltipState?.tooltip?.tooltipID,
  };
};
